<template>
    <div class="inline-block">
        <!-- Trigger Button -->
        <button-base
            style-type="danger"
            v-if="overdueCount > 0"
            @click="openModal"
        >
            <template #icon>
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
            </template>
            Surrender All Overdue Tasks
        </button-base>

        <!-- Modal -->
        <transition
            enter-active-class="transition ease-out duration-300"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-200"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
        >
            <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
                <!-- Backdrop -->
                <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeModal"></div>

                <!-- Modal Content -->
                <div class="flex items-center justify-center min-h-screen p-4">
                    <div class="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all">
                        <!-- Warning Icon -->
                        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                            <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>

                        <!-- Title -->
                        <h3 class="text-lg font-semibold text-gray-900 text-center mb-4">
                            Reschedule All {{ overdueCount }} Overdue Tasks?
                        </h3>

                        <!-- Warning Message -->
                        <div class="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                            <p class="text-sm text-red-800 font-medium mb-2">
                                ⚠️ This action CANNOT be undone!
                            </p>
                            <ul class="text-sm text-red-700 space-y-1 list-disc list-inside">
                                <li>All {{ overdueCount }} overdue tasks will be rescheduled</li>
                                <li>Tasks will be distributed 10 per workday starting tomorrow</li>
                                <li>High priority tasks will be scheduled first</li>
                                <li>Weekends will be automatically skipped</li>
                                <li>This is irreversible - make sure you want to proceed</li>
                            </ul>
                        </div>

                        <!-- Confirmation Checkbox -->
                        <div class="mb-4">
                            <label class="flex items-center">
                                <input
                                    type="checkbox"
                                    v-model="confirmed"
                                    class="rounded border-gray-300 text-red-600 focus:ring-red-500"
                                >
                                <span class="ml-2 text-sm text-gray-700">
                                    I understand this action cannot be undone
                                </span>
                            </label>
                        </div>

                        <!-- Action Buttons -->
                        <div class="flex gap-3">
                            <button
                                @click="closeModal"
                                :disabled="loading"
                                class="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors duration-200 font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                @click="surrenderTasks"
                                :disabled="!confirmed || loading"
                                :class="[
                                    'flex-1 px-4 py-2 rounded-md font-medium transition-colors duration-200',
                                    confirmed && !loading
                                        ? 'bg-red-600 text-white hover:bg-red-700'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                ]"
                            >
                                <span v-if="!loading">Surrender All Tasks</span>
                                <span v-else class="flex items-center justify-center">
                                    <svg class="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Processing...
                                </span>
                            </button>
                        </div>

                        <!-- Success Message -->
                        <transition
                            enter-active-class="transition ease-out duration-300"
                            enter-from-class="transform opacity-0"
                            enter-to-class="transform opacity-100"
                        >
                            <div v-if="successMessage" class="mt-4 p-3 bg-green-100 border border-green-300 rounded-md">
                                <p class="text-sm text-green-800">{{ successMessage }}</p>
                                <div v-if="successDetails" class="mt-2 text-xs text-green-700">
                                    <p>• Tasks rescheduled: {{ successDetails.tasks_rescheduled }}</p>
                                    <p>• Date range: {{ successDetails.start_date }} to {{ successDetails.end_date }}</p>
                                    <p>• Tasks per day: {{ successDetails.tasks_per_day }}</p>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import axios from 'axios';

const props = defineProps({
    overdueCount: {
        type: Number,
        required: true
    },
    route: {
        type: String,
        required: true
    }
});

const showModal = ref(false);
const confirmed = ref(false);
const loading = ref(false);
const successMessage = ref('');
const successDetails = ref(null);

const openModal = () => {
    showModal.value = true;
    confirmed.value = false;
    successMessage.value = '';
    successDetails.value = null;
};

const closeModal = () => {
    if (!loading.value) {
        showModal.value = false;
        if (successMessage.value) {
            // Reload the page after successful rescheduling
            window.location.reload();
        }
    }
};

const surrenderTasks = async () => {
    if (!confirmed.value || loading.value) return;

    loading.value = true;
    successMessage.value = '';
    successDetails.value = null;

    try {
        const response = await axios.post(props.route);

        if (response.data.success) {
            successMessage.value = response.data.message;
            successDetails.value = response.data.details;

            // Auto-close and reload after 3 seconds
            setTimeout(() => {
                closeModal();
            }, 3000);
        } else {
            alert(response.data.message || 'Failed to reschedule tasks');
        }
    } catch (error) {
        console.error('Error rescheduling tasks:', error);
        alert('An error occurred while rescheduling tasks. Please try again.');
    } finally {
        loading.value = false;
    }
};
</script>
